'use client'
import * as React from 'react'
import { Button } from '@repo/design-system-cf/components/button'
import { Typography } from '@repo/design-system-cf/components/typography'
import { useTranslations } from 'next-intl'
import { tw } from 'utils/classnames'
import {
  useListMyAccountNotificationSettingsQuery,
  useSaveMyAccountNotificationSettingsMutation,
} from '@web/gen/user'
import { useIsLoggedInQuery } from 'auth/hooks'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { ToasterCustom } from '@web/components/toaster'
import toast from 'react-hot-toast'
import { Spinner } from '@repo/design-system-cf/components/spinner'
import { CheckCircleIcon } from 'primitives/icons'
import { useNonce } from 'hooks/use-nonce'
import { useSearchParams } from 'next/navigation'
import { useCookies } from 'react-cookie'
import { WORDPRESS_LOGIN_COOKIE_NAME } from 'constants/cookies'

type Variants = 'freebies' | 'daily-gifts'

const settingIdByVariant: Record<Variants, string> = {
  freebies: 'freebies',
  'daily-gifts': 'dailyfreebies',
}

export function EnableNotifications({ variant }: { variant: Variants }) {
  return (
    <React.Suspense>
      <EnableNotificationsAnimate variant={variant} />
      <ToasterCustom />
    </React.Suspense>
  )
}

export function EnableNotificationsAnimate({ variant }: { variant: Variants }) {
  const { isLoading, data: notificationsData } = useListMyAccountNotificationSettingsQuery()
  const [cookies] = useCookies([WORDPRESS_LOGIN_COOKIE_NAME])
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const [shouldShowBanner, setShouldShowBanner] = React.useState(
    Boolean(!cookies[WORDPRESS_LOGIN_COOKIE_NAME]),
  )
  const searchParams = useSearchParams()
  const success = searchParams.get('success')
  const enabledParam = searchParams.get('enable-notifications')
  const configuration = [
    ...(notificationsData?.listMyAccountNotificationSettings?.newsletterNotificationSettings || []),
    ...(notificationsData?.listMyAccountNotificationSettings?.notificationSettings || []),
  ].reduce(
    (acc, setting) => {
      acc[setting.name] = setting
      return acc
    },
    {} as Record<string, { name: string; enabled: boolean }>,
  )
  React.useEffect(() => {
    if (
      (!isLoading && !configuration[settingIdByVariant[variant]]?.enabled) ||
      (success === '1' && enabledParam === settingIdByVariant[variant])
    ) {
      setShouldShowBanner(true)
    }
  }, [isLoading, configuration, variant, success, enabledParam])

  return (
    <div className="relative w-full overflow-hidden">
      <div
        className={tw(
          'transition-[max-height,transform] duration-500 ease-in-out',
          'overflow-hidden',
          {
            'max-h-0 -translate-y-1/2 opacity-0': !shouldShowBanner,
            'max-h-[106px] md:max-h-[60px] translate-y-0 opacity-100': shouldShowBanner,
          },
        )}
      >
        <FreebiesBanner
          useMutation={Boolean(isLoggedIn)}
          settingId={settingIdByVariant[variant]}
          variant={variant}
        />
      </div>
    </div>
  )
}

export function FreebiesBanner({
  settingId,
  useMutation,
  variant,
}: {
  settingId: string
  variant: Variants
  useMutation: boolean
}) {
  const [isUpdatingSetting, setIsUpdatingSetting] = React.useState(false)
  const [isSettingEnabled, setIsSettingEnabled] = React.useState(false)
  const [isFetchingNonce, setIsFetchingNonce] = React.useState(false)
  const t = useTranslations('components.enable_notifications_banner')
  const saveNotificationSettingsMutation = useSaveMyAccountNotificationSettingsMutation()
  const { getNonce } = useNonce()
  const searchParams = useSearchParams()
  const success = searchParams.get('success')
  const enabledParam = searchParams.get('enable-notifications')
  const translationByVariantConfig: Record<
    Variants,
    () => { title_bold: string; title_regular: string; button_text: string }
  > = {
    freebies: () => ({
      title_bold: t('never_miss_another_freebie'),
      title_regular: t('get_notifications'),
      button_text: t('enable_notifications'),
    }),
    'daily-gifts': () => ({
      title_bold: t('never_miss_another_daily_gift'),
      title_regular: t('get_notifications'),
      button_text: t('enable_notifications'),
    }),
  }
  const translationByVariant = translationByVariantConfig[variant]()

  // oxlint-disable-next-line require-await
  const handleEnableNotifications = async () => {
    if (isFetchingNonce || isUpdatingSetting || isSettingEnabled) {
      return
    }
    setIsUpdatingSetting(true)
    try {
      saveNotificationSettingsMutation.mutate(
        {
          input: {
            name: settingId,
            enabled: true,
          },
        },
        {
          // oxlint-disable-next-line require-await
          onSuccess: async data => {
            setIsUpdatingSetting(false)
            if (data?.saveMyAccountNotificationSettings?.error?.description) {
              toast.error(t('save_error'))
              return
            }
            setIsSettingEnabled(true)
          },
          onError: () => {
            setIsUpdatingSetting(false)
            toast.error(t('save_error'))
          },
        },
      )
    } catch (error) {
      setIsUpdatingSetting(false)
      toast.error(t('save_error'))
    }
  }

  const handleEnableNotificationsWithRedirect = async () => {
    if (isFetchingNonce || isUpdatingSetting || isSettingEnabled) {
      return
    }
    setIsFetchingNonce(true)
    const nonce = await getNonce('enable-notifications')
    const url = `${AppRoutes.ENABLE_NOTIFICATIONS}${settingId}/?_wpnonce=${nonce}`
    window.location.href = url
  }

  if (success === '1' && enabledParam === settingId) {
    return (
      <div className="p-3 bg-secondary-500 flex flex-col md:flex-row justify-center items-center gap-2 md:gap-4 md:py-5">
        <div className="flex flex-col md:flex-row justify-center items-center gap-1">
          <Typography variant="cf-title-compact-01" className="uppercase text-cf-white">
            {t('thank_you_for_following_bold')}
          </Typography>
          <Typography variant="body-compact-01-m" className="text-center text-cf-white">
            {t('thank_you_for_following_regular')}
          </Typography>
        </div>
      </div>
    )
  }

  return (
    <div className="p-3 bg-[#E3E1E9] flex flex-col md:flex-row justify-center items-center gap-2 md:gap-4">
      <div className="flex flex-col md:flex-row justify-center items-center gap-1">
        <Typography variant="cf-title-compact-01" className="uppercase">
          {translationByVariant.title_bold}
        </Typography>
        <Typography variant="body-compact-01-m" className="text-center">
          {translationByVariant.title_regular}
        </Typography>
      </div>
      <Button
        tabIndex={isFetchingNonce || isUpdatingSetting || isSettingEnabled ? -1 : 0}
        iconLeft={
          // oxlint-disable-next-line no-nested-ternary
          isFetchingNonce || isUpdatingSetting ? (
            <Spinner className="!h-4 !w-4 animate-spin" />
          ) : isSettingEnabled ? (
            <CheckCircleIcon className="!h-4 !w-4 fill-cf-white" />
          ) : (
            <BellIcon className="!h-4 !w-4" />
          )
        }
        className={tw(
          'uppercase font-bold fill-cf-white bg-[#4118EA] hover:bg-[#2E0BB4] active:bg-[#2E0BB4] rounded',
          {
            'cursor-not-allowed': isFetchingNonce || isUpdatingSetting || isSettingEnabled,
          },
        )}
        onClick={useMutation ? handleEnableNotifications : handleEnableNotificationsWithRedirect}
      >
        {isSettingEnabled ? t('following') : translationByVariant.button_text}
      </Button>
    </div>
  )
}

function BellIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 13 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M6.50138 15.3125C7.32638 15.3125 8.00138 14.6375 8.00138 13.8125H5.00138C5.00138 14.6375 5.66888 15.3125 6.50138 15.3125ZM11.0014 10.8125V7.0625C11.0014 4.76 9.77138 2.8325 7.62638 2.3225V1.8125C7.62638 1.19 7.12388 0.6875 6.50138 0.6875C5.87888 0.6875 5.37638 1.19 5.37638 1.8125V2.3225C3.22388 2.8325 2.00138 4.7525 2.00138 7.0625V10.8125L1.03388 11.78C0.561381 12.2525 0.891381 13.0625 1.55888 13.0625H11.4364C12.1039 13.0625 12.4414 12.2525 11.9689 11.78L11.0014 10.8125Z"
        fill="white"
      />
    </svg>
  )
}
